import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ExternalLink } from "lucide-react";
import keishIcon from "@/assets/icon-green.svg";
import keishIconWhite from "@/assets/icon-white.svg";
import ContactSheet from "@/components/ContactSheet";
import { useTheme } from "@/components/ThemeProvider";
import SEO from "@/components/SEO";
import merLogo from "@/assets/case-studies/mer-logo.svg";
import mer1 from "@/assets/shop/mer-1.avif";
import mer2 from "@/assets/shop/mer-2.avif";
import mer3 from "@/assets/shop/mer-3.avif";
import mer4 from "@/assets/shop/mer-4.avif";
import coterieLogo from "@/assets/shop/coterie-logo.svg";
import coterie1 from "@/assets/shop/coterie-1.jpeg";
import coterie2 from "@/assets/shop/coterie-2.jpg";
import coterie3 from "@/assets/shop/coterie-3.jpg";
import coterie4 from "@/assets/shop/coterie-4.jpg";
import coterie5 from "@/assets/shop/coterie-5.jpg";
import { useState } from "react";

interface FramerTemplate {
  slug: string;
  title: string;
  subtitle: string;
  description: string;
  images: string[];
  logo: string;
  price: string;
  livePreviewUrl: string;
  purchaseUrl: string;
  features: string[];
  category: string;
  caseStudyUrl?: string;
}

const templates: FramerTemplate[] = [
  {
    slug: "mer",
    title: "MĒR",
    subtitle: "Restaurant & Bar Template",
    description:
      "A refined Framer template built for restaurants, bars, and food & beverage brands. Designed with editorial restraint — structured menus, reservation-ready layouts, and a CMS your team can manage without a designer on call.",
    images: [mer1, mer2, mer3, mer4],
    logo: merLogo,
    price: "$40",
    livePreviewUrl: "https://mer.framer.website/",
    purchaseUrl: "https://contra.com/payment-link/Y05MAncr-mer-stunning-eatery-template",
    features: [
      "Fully responsive design",
      "CMS-powered menu system",
      "Reservation / contact integration",
      "Events & specials section",
      "SEO-optimized structure",
      "Easy brand customization",
    ],
    category: "Food & Beverage",
  },
  {
    slug: "coterie",
    title: "COTERIE",
    subtitle: "Real Estate Industry Template",
    description:
      "A modern, refined Framer template built for real estate professionals who want a website that feels as polished as the properties they represent. Complete CMS control over listings, community pages, and room styles — your team updates everything without touching a single line of code.",
    images: [coterie1, coterie2, coterie3, coterie4, coterie5],
    logo: coterieLogo,
    price: "$65",
    livePreviewUrl: "https://coterie.framer.website/",
    purchaseUrl: "https://contra.com/payment-link/D8Ql5hHf-coterie-real-estate-industry-template",
    features: [
      "Full CMS control over all content",
      "Listings & property pages",
      "Community & room styles pages",
      "A11y optimized & responsive",
      "Custom cursors & animations",
      "Automated SEO & analytics",
    ],
    category: "Real Estate",
    caseStudyUrl: "/shop/coterie",
  },
];

const TemplateGallery = ({ images, title }: { images: string[]; title: string }) => {
  const [active, setActive] = useState(0);

  return (
    <div className="mb-6">
      {/* Main image */}
      <div className="relative overflow-hidden rounded-lg mb-3">
        <img
          src={images[active]}
          alt={`${title} preview ${active + 1}`}
          className="w-full aspect-[16/10] object-cover transition-all duration-500"
        />
      </div>
      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                i === active
                  ? "border-foreground/40 opacity-100"
                  : "border-transparent opacity-50 hover:opacity-80"
              }`}
            >
              <img
                src={img}
                alt={`${title} thumbnail ${i + 1}`}
                className="w-16 h-16 md:w-20 md:h-20 object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Shop = () => {
  const { mode } = useTheme();
  const isDark = mode === "dark";

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Shop Templates"
        description="Premium Framer templates by KEISH. Production-ready, CMS-powered, and built with editorial precision."
        canonical="/shop"
      />

      {/* Header */}
      <header
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-5 md:px-10 lg:px-14 py-4 backdrop-blur-xl border-b transition-colors duration-500"
        style={{
          backgroundColor: isDark ? "hsla(0, 0%, 9%, 0.9)" : "hsla(45, 100%, 98%, 0.9)",
          color: isDark ? "hsl(27, 61%, 92%)" : "hsl(0, 0%, 9%)",
          borderColor: isDark ? "hsla(27, 61%, 92%, 0.1)" : "hsl(45, 10%, 88%)",
        }}
      >
        <Link to="/" className="flex items-center gap-2 group">
          <ArrowLeft size={14} className="opacity-50 group-hover:opacity-100 transition-opacity" />
          <img src={isDark ? keishIconWhite : keishIcon} alt="KEISH" className="w-6 h-6" />
          <span className="font-body text-ts-2xs font-semibold tracking-[0.2em] uppercase">KEISH</span>
        </Link>
        <div className="flex items-center gap-4">
          <ContactSheet>
            <button className="font-body text-ts-2xs tracking-[0.15em] uppercase opacity-50 hover:opacity-100 transition-opacity">
              Work With Us
            </button>
          </ContactSheet>
        </div>
      </header>

      {/* Page hero */}
      <div className="pt-28 md:pt-32">
        <div className="max-w-[1000px] mx-auto px-5 md:px-10 pb-12 md:pb-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
          >
            <p className="font-body text-ts-2xs font-medium tracking-[0.25em] uppercase text-muted-foreground mb-3">
              Shop
            </p>
            <h1 className="font-display text-4xl md:text-6xl italic text-foreground mb-3">
              Shop Templates
            </h1>
            <p className="font-body text-ts-sm text-muted-foreground tracking-wide max-w-[520px]">
              Production-ready templates built with the same structure and attention we bring to client work. Plug in your brand and launch.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Template grid */}
      <div className="max-w-[1000px] mx-auto px-5 md:px-10 pb-24">
        <div className="flex flex-col gap-16">
          {templates.map((template, i) => (
            <motion.article
              key={template.slug}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 + i * 0.1, duration: 0.6 }}
              className="group"
            >
              <TemplateGallery images={template.images} title={template.title} />

              {/* Info row */}
              <div className="flex flex-col md:flex-row md:items-start gap-6 md:gap-10">
                {/* Left: meta */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <img src={template.logo} alt="" className="w-6 h-6" />
                    <span className="font-body text-ts-2xs tracking-[0.15em] uppercase text-muted-foreground">
                      {template.category}
                    </span>
                  </div>
                  <h2 className="font-display text-2xl md:text-3xl italic text-foreground mb-2">
                    {template.title}
                  </h2>
                  <p className="font-body text-ts-xs text-muted-foreground mb-1">
                    {template.subtitle}
                  </p>
                  <p className="font-body text-ts-xs text-foreground/80 leading-relaxed mt-3">
                    {template.description}
                  </p>

                  {/* Features */}
                  <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                    {template.features.map((f) => (
                      <li
                        key={f}
                        className="font-body text-ts-2xs text-muted-foreground flex items-center gap-2"
                      >
                        <span className="w-1 h-1 rounded-full bg-foreground/20 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Right: price + actions */}
                <div className="flex flex-col items-start md:items-end gap-3 md:pt-8 flex-shrink-0">
                  <span className="font-heading text-2xl md:text-3xl font-bold text-foreground">
                    {template.price}
                  </span>

                  <a
                    href={template.purchaseUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-body text-ts-2xs font-medium tracking-[0.15em] uppercase transition-colors hover:bg-primary/90"
                  >
                    Buy Template
                    <ArrowUpRight size={12} />
                  </a>

                  <a
                    href={template.livePreviewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 font-body text-ts-2xs tracking-[0.1em] uppercase text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Live Preview
                    <ExternalLink size={10} />
                  </a>

                  <Link
                    to={template.caseStudyUrl || `/work/${template.slug}`}
                    className="inline-flex items-center gap-1.5 font-body text-ts-2xs tracking-[0.1em] uppercase text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Case Study
                    <ArrowUpRight size={10} />
                  </Link>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Shop;
